/* ============================================================
   ATHLO+ — members-only points, disclosed BEFORE payment
   (cart drawer + cart page). ADDON, 2026-09-30.

   Owner decision on hard-launch night: guest checkout goes on,
   and points on an order are for MEMBERS. A guest order earns
   nothing and is never credited later — so the rule has to be
   on screen next to the subtotal, not sprung after payment.

   snippets/hub-cart-points.liquid renders the line with the 1×
   figure (right before any JS runs). This file:
     • recomputes it with the SAME rule the backend awards by,
     • applies a signed-in member's tier multiplier from the
       verified loyalty state (window.ATHLO_LOYALTY, filled by
       hub-loyalty.js from ?state),
     • repaints the drawer when the bag changes (hub-shop.js
       fires `hub:cart` from its paint()).

   THE NUMBER MUST NOT DRIFT FROM THE AWARD. earnPoints() and
   cartEarnInputs() below mirror earnPoints() / orderEarnInputs()
   in supabase-community/supabase/functions/loyalty/core.ts,
   operation for operation (same float order, same floor), and
   _verify/loyalty-members-only-test.ts runs both sides over a
   grid and fails on any difference. Change one, change both.

   Degrades silently: no JSON, no loyalty state, a bad cart
   payload — it leaves whatever Liquid rendered (or hides the
   line when the figure is 0). ES5 on purpose (see
   _verify/es5-storefront-test.mjs). Remove: the snippet + its two
   renders + this file + the dispatch line in hub-shop.js.
   ============================================================ */
(function () {
  'use strict';
  if (window.HubCartPoints) return;

  // MIRROR of core.ts earnPoints(): floor((subtotal - giftCards) x multiplier), never negative.
  function earnPoints(subtotal, giftCardTotal, mult) {
    var earnBase = Math.max(0, (subtotal || 0) - (giftCardTotal || 0));
    return { earnBase: earnBase, points: Math.floor(earnBase * (mult || 1)) };
  }

  // MIRROR of core.ts orderEarnInputs(), read off a Shopify cart (/cart.js shape) instead of an
  // order: the subtotal AFTER discounts (cart.total_price, cents), and gift-card lines at their
  // PRE-discount unit price x quantity (original_price, cents). cents/100 is the same double as
  // parseFloat("12.34"), so the two sides do identical arithmetic.
  function cartEarnInputs(cart) {
    var subtotal = ((cart && cart.total_price) || 0) / 100;
    var giftCardTotal = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (li && li.gift_card) giftCardTotal += ((li.original_price || 0) / 100) * (li.quantity || 1);
    }
    return { subtotal: subtotal, giftCardTotal: giftCardTotal };
  }

  // A signed-in member earns at their tier's multiplier (flat mode already folded in server-side);
  // anyone else is quoted the 1x a new member starts at.
  function multiplierFor(isMember) {
    if (!isMember) return 1;
    var s = window.ATHLO_LOYALTY;
    return (s && typeof s.multiplier === 'number' && s.multiplier > 0) ? s.multiplier : 1;
  }

  function pointsFor(cart, isMember) {
    var inp = cartEarnInputs(cart);
    var mult = multiplierFor(isMember);
    return earnPoints(inp.subtotal, inp.giftCardTotal, mult).points + bundleBonusFor(cart, mult);
  }

  // === ADDON 2026-11-03: double points on the 11.11 bundles ===
  // Mirrors core.ts bundleBonus(): during the window, bundle lines earn a second time at the same
  // multiplier. In the cart a bundle is ONE line (product type "Bundle", only the 11.11 bundles use
  // it); the order splits it into components, but both sides sum the bundle after its discount.
  // Window in UTC = 10 Nov 8pm SGT to 11 Nov 23:59:59 SGT, the same as BUNDLE_BONUS_WINDOWS.
  // Remove this function and the "+ bundleBonusFor(...)" above to revert.
  var BUNDLE_BONUS_FROM = Date.UTC(2026, 10, 10, 12, 0, 0), BUNDLE_BONUS_TO = Date.UTC(2026, 10, 11, 16, 0, 0);
  function bundleBonusFor(cart, mult) {
    var t = new Date().getTime();
    if (t < BUNDLE_BONUS_FROM || t >= BUNDLE_BONUS_TO) return 0;
    var cents = 0, items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (li && !li.gift_card && li.product_type === 'Bundle') cents += Math.max(0, li.final_line_price || 0);
    }
    return Math.floor((cents / 100) * (mult || 1));
  }

  var lastCart = null; // newest bag from hub-shop.js, if any

  function ownCart(el) {
    try {
      var node = el.querySelector('[data-cart-points-cart]');
      return node ? JSON.parse(node.textContent || 'null') : null;
    } catch (e) { return null; }
  }

  function paintOne(el, cart) {
    if (!el || !cart || typeof cart.total_price !== 'number' || !cart.items) return;
    var member = el.getAttribute('data-member') === '1';
    var pts = pointsFor(cart, member);
    var n = el.querySelector('[data-cart-points-n]');
    var unit = el.querySelector('[data-cart-points-unit]');
    if (n) n.textContent = pts.toLocaleString ? pts.toLocaleString() : String(pts);
    if (unit) unit.textContent = pts === 1 ? 'point' : 'points';
    el.hidden = !(pts > 0);
  }

  function paintAll() {
    var els = document.querySelectorAll('[data-cart-points]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      // The drawer follows the live bag; the cart page is a fresh render per change, so its own
      // embedded cart is always current.
      var cart = (el.getAttribute('data-surface') === 'drawer' && lastCart) ? lastCart : ownCart(el);
      try { paintOne(el, cart); } catch (e) { /* leave the Liquid render in place */ }
    }
  }

  document.addEventListener('hub:cart', function (e) {
    var cart = e && e.detail;
    if (cart && typeof cart.total_price === 'number' && cart.items) lastCart = cart;
    paintAll();
  });
  // The member's multiplier arrives after first paint (hub-loyalty.js -> ?state).
  window.addEventListener('athlo:loyalty', paintAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintAll);
  else paintAll();

  window.HubCartPoints = { earnPoints: earnPoints, cartEarnInputs: cartEarnInputs, pointsFor: pointsFor, repaint: paintAll };
})();
